
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './auth';
import type { RealtimePresenceState } from '@supabase/supabase-js';

interface PresenceContextType {
  onlineUsers: Set<string>; // Ensemble des IDs des utilisateurs en ligne
}

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

interface PresenceProviderProps {
  children: ReactNode;
}

export const PresenceProvider: React.FC<PresenceProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) {
      setOnlineUsers(new Set()); // Réinitialiser si l'utilisateur se déconnecte
      return;
    }

    const channel = supabase.channel('online-users', {
      config: {
        presence: {
          key: user.id, // Utiliser l'ID de l'utilisateur comme clé unique
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const newState: RealtimePresenceState = channel.presenceState();
        console.log('Presence sync:', newState);
        const users = new Set(
          Object.keys(newState).filter(id => id !== user.id) // Exclure l'utilisateur actuel
        );
        setOnlineUsers(users);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('Presence join:', key, newPresences);
        setOnlineUsers(prev => new Set([...prev, key]));
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('Presence leave:', key, leftPresences);
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          const status = await channel.track({ online_at: new Date().toISOString() });
          console.log('Presence tracking status:', status);
        } else if (status === 'CLOSED') {
          console.log('Presence channel closed');
        } else {
           console.log('Presence subscription status:', status)
        }
      });

    // Nettoyage
    return () => {
      if (channel) {
        supabase.removeChannel(channel).catch(err => 
          console.error("Error removing presence channel:", err)
        );
      }
    };
  }, [user]); // Se réabonner si l'utilisateur change

  return (
    <PresenceContext.Provider value={{ onlineUsers }}>
      {children}
    </PresenceContext.Provider>
  );
};

// Hook pour utiliser le contexte de présence
export const usePresence = (): PresenceContextType => {
  const context = useContext(PresenceContext);
  if (context === undefined) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
}; 
